import { Link, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { DecoratedClient } from '@/db/types/client';
import { formatDate } from '@/utils/date_utils';

export interface ClientTableContentProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filteredClients: DecoratedClient[] | undefined;
  unsyncedClientIds: Set<number>;
}

function SearchInput({ searchQuery, setSearchQuery }: Pick<ClientTableContentProps, 'searchQuery' | 'setSearchQuery'>) {
  return (
    <div className="relative mb-3">
      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        type="text"
        aria-label="Search clients"
        placeholder="Search by client's name"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') setSearchQuery(''); }}
        className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
      />
      {searchQuery && (
        <button
          type="button"
          onClick={() => setSearchQuery('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          aria-label="Clear search"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function LoadingPlaceholder() {
  return (
    <tr>
      <td colSpan={3} className="py-8 text-center text-gray-500">Loading clients...</td>
    </tr>
  );
}

function EmptyPlaceholder() {
  return (
    <tr>
      <td colSpan={3} className="py-8 text-center text-gray-500">No clients found</td>
    </tr>
  );
}

function ClientRows({ filteredClients, unsyncedClientIds }: { filteredClients: DecoratedClient[]; unsyncedClientIds: Set<number> }) {
  const navigate = useNavigate();
  if (filteredClients.length === 0) return <EmptyPlaceholder />;

  return <>{filteredClients.map(client => {
    const hasUnsynced = unsyncedClientIds.has(client.id);
    const href = `/clients/${client.label}`;
    return (
      <tr
        key={client.id}
        onClick={() => navigate(href, { state: { from: '/' } })}
        onKeyDown={e => { if (e.key === 'Enter') navigate(href, { state: { from: '/' } }); }}
        tabIndex={0}
        className="border-b border-gray-100 hover:bg-gray-50 transition-colors text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500"
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            {hasUnsynced && (
              <>
                <ExclamationTriangleIcon aria-hidden="true" className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                <span className="sr-only">has unsynced changes</span>
              </>
            )}
            <span className="truncate text-gray-900">{client.person.displayName}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-gray-600">{client.primaryPhone}</td>
        <td className="px-4 py-3 text-gray-600">{formatDate(client.created_at)}</td>
      </tr>
    );
  })}</>;
}

export function ClientTableContent({ searchQuery, setSearchQuery, filteredClients, unsyncedClientIds }: ClientTableContentProps) {
  const loading = filteredClients === undefined;
  return (
    <div className="flex-1 flex flex-col min-w-0">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">
          My Clients (Top 100)
        </h2>
        <Link to="/clients" className="text-sm gds--link link--secondary">
          See All
        </Link>
      </div>

      <SearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />

      <div className="bg-white rounded-lg border border-gray-200 flex-1 min-h-0 overflow-y-auto shadow-sm">
        <table className="w-full table-fixed" aria-label="My Clients">
          <thead className="sticky top-0 bg-gray-50">
            <tr className="border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 py-2 text-left font-medium">Client</th>
              <th className="px-4 py-2 text-left font-medium">Phone Number</th>
              <th className="px-4 py-2 text-left font-medium">Client Since</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <LoadingPlaceholder /> : <ClientRows filteredClients={filteredClients!} unsyncedClientIds={unsyncedClientIds} />}
          </tbody>
        </table>
      </div>
    </div>
  );
}

